/**
 * Shared stats chart utilities
 * Pure functions for stat computation, formatting, and chart summary.
 * Used by both web StatsBarChart and mobile StatsBarChart.
 */

import { lineOutcome, overUnderStreak } from './lineOutcome';
import { impliedProbability, normalizeAmericanOdds } from './oddsUtils';
import { type CalendarDay, easternDay, formatCalendarDay, gameDayTimestamp } from './time';

// =============================================================================
// Types
// =============================================================================

/** Chart period derived from market key */
export type ChartPeriod = 'full' | 'q1' | 'q2' | 'q3' | 'q4' | 'h1' | 'h2';

/** Quarter score entry for a single period in a team game */
export interface QuarterScoreEntry {
  period: number;
  teamScore: number;
  opponentScore: number;
}

/** A player's newest played games that generation gives quarter lines, and the prompt's quarter average reads. */
export const RECENT_PERIOD_GAMES = 5;

/** Per-quarter player stats for period-specific charts */
export interface PeriodPlayerStats {
  pts?: number;
  reb?: number;
  ast?: number;
  stl?: number;
  blk?: number;
  fg3m?: number;
  fgm?: number;
  ftm?: number;
}

/** Minimal game data shape for stat value extraction */
export interface GameStatRow {
  pts?: number;
  reb?: number;
  ast?: number;
  stl?: number;
  blk?: number;
  fg3m?: number;
  fgm?: number;
  ftm?: number;
  dnp?: boolean;
  /** Per-quarter player stats for period-specific charts */
  periodStats?: Partial<Record<'q1' | 'q2' | 'q3' | 'q4', PeriodPlayerStats>>;
  [key: string]: unknown;
}

/** Player game data for charts — extends GameStatRow with game context */
export interface GameData extends GameStatRow {
  date?: string;
  opponent?: string;
  min?: string;
  isHome?: boolean;
  teamScore?: number;
  opponentScore?: number;
  won?: boolean;
  /** An NBA preseason game, shown during the preseason window. */
  isPreseason?: boolean;
}

/** What stats cover in the NBA preseason window: last regular season, plus this preseason's games. */
export interface PreseasonWindow {
  regularSeason: string;
  regularSeasonGames: number;
  preseasonGames: number;
}

/** "2025-26 season + 2 preseason games", "2025-26 season", or "3 preseason games" for a rookie. */
export function preseasonWindowLabel(window: PreseasonWindow): string {
  const preseason = `${window.preseasonGames} preseason game${window.preseasonGames === 1 ? '' : 's'}`;
  if (window.regularSeasonGames === 0) return preseason;
  const season = `${window.regularSeason} season`;
  return window.preseasonGames === 0 ? season : `${season} + ${preseason}`;
}

/** The note stat views show under a player's averages; preseason games are marked with `*`. */
export function preseasonWindowNote(window: PreseasonWindow): string {
  const marker = window.preseasonGames > 0 ? ', * marks a preseason game' : '';
  return `${preseasonWindowLabel(window)}${marker}. No regular-season games yet.`;
}

/** Shown under a chart when any of its games carries the `*` from gameChartLabel. */
export const PRESEASON_GAME_FOOTNOTE = '* marks a preseason game.';

/** A game's chart label, with `*` on a preseason game. */
export function gameChartLabel(label: string, isPreseason: boolean | undefined): string {
  return isPreseason ? `${label}*` : label;
}

/** Team game data for charts — spread/total/moneyline display */
export interface TeamGameData {
  date?: string;
  opponent?: string;
  opponentAbbr?: string;
  isHome: boolean;
  teamScore: number;
  opponentScore: number;
  margin: number;
  gameTotal: number;
  won: boolean;
  quarterScores?: Array<{ period: number; teamScore: number; opponentScore: number }>;
}

/** Pre-formatted string averages for display (matches server calculateAverages output) */
export interface StatAverages {
  pts: string;
  reb: string;
  ast: string;
  fg3m: string;
  blk: string;
  stl: string;
  turnover: string;
  bs: string;
  pra: string;
  pr: string;
  pa: string;
  ra: string;
  games: number;
}

export interface ChartSummary {
  avg: number;
  median: number;
  streak: number;
  streakType: 'over' | 'under' | null;
  trendDirection: 'up' | 'down' | 'flat';
}

export interface NiceTicksResult {
  ticks: number[];
  minValue: number;
  maxValue: number;
}

// =============================================================================
// Constants
// =============================================================================

export const STAT_SHORT_LABELS: Record<string, string> = {
  pts: 'pts',
  reb: 'reb',
  ast: 'ast',
  stl: 'stl',
  blk: 'blk',
  fg3m: '3PM',
  fgm: 'FGM',
  ftm: 'FTM',
  pra: 'PRA',
  pr: 'P+R',
  pa: 'P+A',
  ra: 'R+A',
  bs: 'B+S',
};

/** All stat keys available for chart selection */
export const CHART_STAT_KEYS = [
  'pts',
  'reb',
  'ast',
  'stl',
  'blk',
  'fg3m',
  'pra',
  'pr',
  'pa',
  'ra',
] as const;

// =============================================================================
// Functions
// =============================================================================

/** Compute stat value for a game, handling combo stats (PRA, P+R, etc.) */
export function getStatValue(game: GameStatRow, statKey: string): number {
  if (!game) return 0;
  switch (statKey) {
    case 'pra':
      return (game.pts || 0) + (game.reb || 0) + (game.ast || 0);
    case 'pr':
      return (game.pts || 0) + (game.reb || 0);
    case 'pa':
      return (game.pts || 0) + (game.ast || 0);
    case 'ra':
      return (game.reb || 0) + (game.ast || 0);
    case 'bs':
      return (game.blk || 0) + (game.stl || 0);
    default:
      return (game[statKey] as number) ?? 0;
  }
}

/**
 * The game day of a game-log date: a bare `YYYY-MM-DD` (BDL NBA) or a tip timestamp (BDL WNBA,
 * generated picks), whose game day is its Eastern day. Null for missing or unparseable input
 * (callers render an empty label).
 */
function gameDayOf(dateStr: string | undefined): CalendarDay | null {
  if (!dateStr) return null;
  try {
    const day = easternDay(dateStr);
    return Number.isNaN(gameDayTimestamp(day).getTime()) ? null : day;
  } catch {
    return null;
  }
}

/** Format date string to short format: "Jan 5" */
export function formatDate(dateStr: string | undefined): string {
  const day = gameDayOf(dateStr);
  return day ? formatCalendarDay(day, { month: 'short', day: 'numeric' }) : '';
}

/** Format date string to compact format for dense charts: "3/5" (month/day) */
export function formatDateCompact(dateStr: string | undefined): string {
  const day = gameDayOf(dateStr);
  return day ? `${Number(day.slice(5, 7))}/${Number(day.slice(8, 10))}` : '';
}

/** Format date string to long format: "Jan 5, 2026" */
export function formatDateLong(dateStr: string | undefined): string {
  const day = gameDayOf(dateStr);
  return day ? formatCalendarDay(day, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

/** Compute Y-axis ticks at nice intervals (1, 2, 5, 10, 20, ...) */
export function computeNiceTicks(
  dataMin: number,
  dataMax: number,
  allowNegative = false,
): NiceTicksResult {
  if (dataMax <= dataMin) {
    const c = Math.round(dataMax);
    const s = Math.abs(c) > 10 ? 5 : 1;
    const lo = allowNegative ? c - s : Math.max(0, c - s);
    return { ticks: [c + s, c, lo], minValue: lo, maxValue: c + s };
  }

  const rawRange = dataMax - dataMin;
  const rawStep = rawRange / 3;
  const mag = 10 ** Math.floor(Math.log10(rawStep));
  const norm = rawStep / mag;
  let step: number;
  if (norm <= 1.5) step = mag;
  else if (norm <= 3) step = 2 * mag;
  else if (norm <= 6) step = 5 * mag;
  else step = 10 * mag;
  step = Math.max(1, step);

  // Snap bounds to step multiples (provides natural padding)
  const niceMax = Math.ceil((dataMax + step * 0.1) / step) * step;
  const niceMin = allowNegative
    ? Math.floor(dataMin / step) * step
    : Math.max(0, Math.floor(dataMin / step) * step);

  const ticks: number[] = [];
  for (let v = niceMax; v >= niceMin - step * 0.001; v -= step) {
    ticks.push(Math.round(v));
  }

  return { ticks, minValue: ticks[ticks.length - 1], maxValue: ticks[0] };
}

/**
 * Compute chart summary stats: average, median, streak, and trend.
 * @param values - Array of stat values (non-DNP games only, in chronological order)
 * @param referenceLine - Optional reference line for streak calculation (e.g., prop line or average)
 */
export function computeChartSummary(values: number[], referenceLine?: number): ChartSummary {
  if (values.length === 0) {
    return { avg: 0, median: 0, streak: 0, streakType: null, trendDirection: 'flat' };
  }

  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const sorted = [...values].sort((a, b) => a - b);
  const median =
    sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];

  // Against referenceLine, or the average, as a plain over/under line; a chart on an alternate
  // leg reads computeStreak with its flag.
  const { streak, streakType } = computeStreak(values, referenceLine ?? avg, false);

  // Trend: compare last 3 avg vs first 3 avg (if enough data)
  let trendDirection: 'up' | 'down' | 'flat' = 'flat';
  if (values.length >= 4) {
    const recent3 = values.slice(-3).reduce((a, v) => a + v, 0) / 3;
    const older3 = values.slice(0, 3).reduce((a, v) => a + v, 0) / 3;
    const diff = recent3 - older3;
    if (Math.abs(diff) > avg * 0.05) {
      trendDirection = diff > 0 ? 'up' : 'down';
    }
  }

  return { avg, median, streak, streakType, trendDirection };
}

// =============================================================================
// Standalone streak computation (for "fixed" streak from full dataset)
// =============================================================================

/**
 * Compute streak independently from full chart summary.
 * Used to calculate streak from ALL available games (not just the windowed view).
 * A push (a game on a standard line) neither extends nor breaks the run.
 * @param values - All stat values in chronological order (oldest first)
 * @param referenceLine - The prop line (or average) to compare against
 * @param isAlternateLine - The leg's own alternate X+ line (isAlternateChartLine), where equal is an over
 */
export function computeStreak(
  values: number[],
  referenceLine: number,
  isAlternateLine: boolean,
): { streak: number; streakType: 'over' | 'under' | null } {
  const signed = overUnderStreak(
    values.map((v) => lineOutcome(v, referenceLine, isAlternateLine)).reverse(),
  );
  if (signed === null) return { streak: 0, streakType: null };
  return { streak: Math.abs(signed), streakType: signed > 0 ? 'over' : 'under' };
}

// =============================================================================
// Summary stat definitions (shared between web and mobile)
// =============================================================================

/** Summary stat pill definition */
export interface SummaryStatDef {
  id: string;
  label: string;
  /** Info tooltip description */
  description: string;
  /** Whether this stat is only shown when certain conditions are met */
  conditional?: boolean;
}

/** Player chart summary stat definitions (Avg, Med, Streak, Trend, Stat vs Line) */
export const PLAYER_SUMMARY_STAT_DEFS: SummaryStatDef[] = [
  { id: 'avg', label: 'Avg', description: 'Mean value over displayed games' },
  { id: 'med', label: 'Med', description: 'Middle value (less skewed by outliers)' },
  {
    id: 'streak',
    label: 'Streak',
    description:
      'Consecutive games over/under the line among the games shown; a game landing on the line (a push) neither extends nor breaks it',
  },
  {
    id: 'trend',
    label: 'Trend',
    description: 'Recent 3-game avg vs older games — color reflects bet direction',
  },
  { id: 'statVsLine', label: '', description: 'How the average compares to the prop line' },
];

/** Team chart summary stat definitions */
export const TEAM_SUMMARY_STAT_DEFS: SummaryStatDef[] = [
  { id: 'avg', label: 'Avg', description: 'Average value over displayed games' },
  { id: 'med', label: 'Med', description: 'Middle value (less affected by outliers)' },
  {
    id: 'streak',
    label: 'Streak',
    description:
      'Current streak of consecutive hits or misses against the line (uses full game history); a push neither extends nor breaks it',
  },
  { id: 'home', label: 'Home', description: 'Hit rate at home (hits/total games at home)' },
  { id: 'away', label: 'Away', description: 'Hit rate on the road (hits/total away games)' },
  {
    id: 'statVsLine',
    label: '',
    description: 'Difference between average and prop line (green = favorable)',
  },
];

// =============================================================================
// Phase 4: Advanced chart features
// =============================================================================

// 4a: Home/Away filter
// -----------------------------------------------------------------------------

export type LocationFilter = 'all' | 'home' | 'away';

/** Filter games by home/away. Returns all games when isHome is undefined (stale cache fallback). */
export function filterGamesByLocation<T extends { isHome?: boolean; dnp?: boolean }>(
  games: T[],
  filter: LocationFilter,
): T[] {
  if (filter === 'all') return games;
  return games.filter((g) => {
    if (g.isHome === undefined) return true; // Unknown location → include in both
    return filter === 'home' ? g.isHome : !g.isHome;
  });
}

export interface HomeSplits {
  homeGames: number;
  awayGames: number;
  homeHitRate: number;
  awayHitRate: number;
  homeAvg: number;
  awayAvg: number;
}

/** Compute home vs away splits for hit rate, average, and game count */
export function computeHomeSplits(
  games: GameStatRow[],
  statKey: string,
  propLine: number,
  isUnderBet = false,
): HomeSplits {
  const home: number[] = [];
  const away: number[] = [];
  for (const g of games) {
    if (g.dnp) continue;
    const v = getStatValue(g, statKey);
    const isHome = (g as Record<string, unknown>).isHome;
    if (isHome === true) home.push(v);
    else if (isHome === false) away.push(v);
  }
  const hitCheck = (v: number) => (isUnderBet ? v < propLine : v >= propLine);
  return {
    homeGames: home.length,
    awayGames: away.length,
    homeHitRate: home.length > 0 ? home.filter(hitCheck).length / home.length : 0,
    awayHitRate: away.length > 0 ? away.filter(hitCheck).length / away.length : 0,
    homeAvg: home.length > 0 ? home.reduce((a, b) => a + b, 0) / home.length : 0,
    awayAvg: away.length > 0 ? away.reduce((a, b) => a + b, 0) / away.length : 0,
  };
}

// 4b: Minutes parsing
// -----------------------------------------------------------------------------

/** Parse minutes string ("34:12", "34", "0", undefined) → number. Returns 0 for DNP/missing. */
export function parseMinutes(min: string | undefined): number {
  if (!min) return 0;
  const trimmed = min.trim();
  if (!trimmed || trimmed === '0' || trimmed === '00:00') return 0;
  // Format "MM:SS"
  const colonMatch = trimmed.match(/^(\d+):(\d+)$/);
  if (colonMatch) return parseInt(colonMatch[1], 10);
  // Plain number
  const num = parseFloat(trimmed);
  return Number.isFinite(num) ? Math.round(num) : 0;
}

export interface MinutesTrendPoint {
  gameIndex: number;
  minutes: number;
  dnp: boolean;
}

/** Compute minutes trend points for overlay line. DNP games have dnp=true, minutes=0. */
export function computeMinutesTrend(
  games: Array<{ min?: string; dnp?: boolean }>,
): MinutesTrendPoint[] {
  return games.map((g, i) => ({
    gameIndex: i,
    minutes: g.dnp ? 0 : parseMinutes(g.min),
    dnp: !!g.dnp,
  }));
}

// 4d: Sparkline
// -----------------------------------------------------------------------------

/** Compute SVG polyline points string for a mini sparkline from game values.
 * Returns a `points` string for `<polyline>`, e.g. "0,18 3,12 6,8 ..."
 */
export function computeSparklinePoints(
  games: GameStatRow[],
  statKey: string,
  width: number,
  height: number,
): string {
  const played = games.filter((g) => !g.dnp);
  if (played.length < 2) return '';
  const values = played.map((g) => getStatValue(g, statKey));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const padding = 2; // px padding top/bottom
  const usableH = height - padding * 2;
  return values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = padding + usableH - ((v - min) / range) * usableH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

// 4e: Heatmap
// -----------------------------------------------------------------------------

export type HeatmapStatus = 'hit' | 'miss' | 'dnp';

export interface HeatmapCell {
  status: HeatmapStatus;
  value: number;
  date?: string;
  opponent?: string;
}

/** Compute heatmap cells for a compact hit/miss row. */
export function computeHeatmapCells(
  games: Array<GameStatRow & { date?: string; opponent?: string }>,
  statKey: string,
  propLine: number,
  isUnderBet = false,
): HeatmapCell[] {
  return games.map((g) => {
    if (g.dnp) {
      return { status: 'dnp' as const, value: 0, date: g.date, opponent: g.opponent };
    }
    const value = getStatValue(g, statKey);
    const isHit = isUnderBet ? value < propLine : value >= propLine;
    return {
      status: isHit ? ('hit' as const) : ('miss' as const),
      value,
      date: g.date,
      opponent: g.opponent,
    };
  });
}

// 4f: Compare mode
// -----------------------------------------------------------------------------

export interface GameComparison {
  delta: number;
  percentChange: number;
  game1Value: number;
  game2Value: number;
  game1Date?: string;
  game2Date?: string;
  game1Opponent?: string;
  game2Opponent?: string;
}

/** Compare two games on a stat, returning the delta and percent change. */
export function computeGameComparison(
  game1: GameStatRow & { date?: string; opponent?: string },
  game2: GameStatRow & { date?: string; opponent?: string },
  statKey: string,
): GameComparison {
  const v1 = getStatValue(game1, statKey);
  const v2 = getStatValue(game2, statKey);
  const delta = v2 - v1;
  const percentChange = v1 !== 0 ? (delta / v1) * 100 : v2 !== 0 ? 100 : 0;
  return {
    delta,
    percentChange,
    game1Value: v1,
    game2Value: v2,
    game1Date: game1.date,
    game2Date: game2.date,
    game1Opponent: game1.opponent,
    game2Opponent: game2.opponent,
  };
}

// 4g: Value indicator (hit rate vs implied probability from odds)
// -----------------------------------------------------------------------------

export interface ValueIndicator {
  /** Whether the historical hit rate exceeds implied probability (positive edge) */
  hasValue: boolean;
  /** Edge = hitRate - impliedProb. Positive = value bet, negative = overpriced. */
  edge: number;
  /** Formatted label, e.g. "+28% edge" or "-12% edge" */
  label: string;
  /** The implied probability from odds (0-1) */
  impliedProb: number;
}

/** Compute value indicator comparing historical hit rate against implied probability from odds.
 * @param hitRate - Historical hit rate as decimal (0-1), e.g. 0.8 for 80%
 * @param odds - American odds string, e.g. "-110", "+150"
 */
export function computeValueIndicator(hitRate: number, odds: string): ValueIndicator | null {
  const normalized = normalizeAmericanOdds(odds);
  if (!normalized) return null;
  const impliedProb = impliedProbability(normalized);
  if (!Number.isFinite(impliedProb) || impliedProb <= 0) return null;
  const edge = hitRate - impliedProb;
  const edgePct = Math.round(edge * 100);
  return {
    hasValue: edge > 0,
    edge,
    label: `${edgePct > 0 ? '+' : ''}${edgePct}% edge`,
    impliedProb,
  };
}

// =============================================================================
// Period-specific chart utilities
// =============================================================================

/** Period suffix patterns in market keys */
const PERIOD_SUFFIXES: Record<string, ChartPeriod> = {
  _q1: 'q1',
  _q2: 'q2',
  _q3: 'q3',
  _q4: 'q4',
  _h1: 'h1',
  _h2: 'h2',
};

/**
 * Extract chart period from a market key.
 * Returns 'full' for full-game markets, or the specific period.
 *
 * Examples:
 *   'player_points' → 'full'
 *   'player_points_q1' → 'q1'
 *   'totals_h1' → 'h1'
 *   'spreads_q3' → 'q3'
 *   'team_totals_h2' → 'h2'
 */
export function marketKeyToChartPeriod(marketKey: string | undefined): ChartPeriod {
  if (!marketKey) return 'full';
  for (const [suffix, period] of Object.entries(PERIOD_SUFFIXES)) {
    if (marketKey.endsWith(suffix)) return period;
  }
  return 'full';
}

/** Team period stat result computed from quarter scores */
export interface TeamPeriodStats {
  teamScore: number;
  opponentScore: number;
  margin: number;
  gameTotal: number;
}

/**
 * Compute team stats for a specific period from quarter scores.
 *
 * @param quarterScores - Array of quarter score entries (periods 1-4)
 * @param period - The chart period to compute
 * @returns Period stats, or null if required quarter data is missing
 */
export function getTeamPeriodStats(
  quarterScores: QuarterScoreEntry[] | undefined,
  period: ChartPeriod,
): TeamPeriodStats | null {
  if (!quarterScores || quarterScores.length === 0) return null;

  const byPeriod = new Map<number, QuarterScoreEntry>();
  for (const qs of quarterScores) {
    byPeriod.set(qs.period, qs);
  }

  let teamScore = 0;
  let opponentScore = 0;

  switch (period) {
    case 'full':
      return null; // Full-game uses existing data, not quarter scores
    case 'q1':
    case 'q2':
    case 'q3':
    case 'q4': {
      const qNum = parseInt(period[1], 10);
      const q = byPeriod.get(qNum);
      if (!q) return null;
      teamScore = q.teamScore;
      opponentScore = q.opponentScore;
      break;
    }
    case 'h1': {
      const q1 = byPeriod.get(1);
      const q2 = byPeriod.get(2);
      if (!q1 || !q2) return null;
      teamScore = q1.teamScore + q2.teamScore;
      opponentScore = q1.opponentScore + q2.opponentScore;
      break;
    }
    case 'h2': {
      const q3 = byPeriod.get(3);
      const q4 = byPeriod.get(4);
      if (!q3 || !q4) return null;
      teamScore = q3.teamScore + q4.teamScore;
      opponentScore = q3.opponentScore + q4.opponentScore;
      break;
    }
  }

  return {
    teamScore,
    opponentScore,
    margin: teamScore - opponentScore,
    gameTotal: teamScore + opponentScore,
  };
}

/**
 * Get stat value for a game with period support.
 * When a period is specified and periodStats exist, returns period-specific values.
 * Falls back to full-game stats when period data is unavailable.
 */
export function getStatValueForPeriod(
  game: GameStatRow,
  statKey: string,
  period: ChartPeriod,
): { value: number; hasPeriodData: boolean } {
  if (!game) return { value: 0, hasPeriodData: false };

  // Full game → use regular getStatValue
  if (period === 'full') {
    return { value: getStatValue(game, statKey), hasPeriodData: true };
  }

  // Half periods for player stats: sum the two quarters
  if (period === 'h1' || period === 'h2') {
    const quarters = period === 'h1' ? (['q1', 'q2'] as const) : (['q3', 'q4'] as const);
    const q1Stats = game.periodStats?.[quarters[0]];
    const q2Stats = game.periodStats?.[quarters[1]];
    if (!q1Stats || !q2Stats) {
      return { value: getStatValue(game, statKey), hasPeriodData: false };
    }
    const v1 = getPeriodStatValue(q1Stats, statKey);
    const v2 = getPeriodStatValue(q2Stats, statKey);
    return { value: v1 + v2, hasPeriodData: true };
  }

  // Quarter period
  const quarterKey = period as 'q1' | 'q2' | 'q3' | 'q4';
  const periodData = game.periodStats?.[quarterKey];
  if (!periodData) {
    return { value: getStatValue(game, statKey), hasPeriodData: false };
  }

  return { value: getPeriodStatValue(periodData, statKey), hasPeriodData: true };
}

/** Extract a stat value from period player stats, handling combo stats */
function getPeriodStatValue(stats: PeriodPlayerStats, statKey: string): number {
  switch (statKey) {
    case 'pra':
      return (stats.pts || 0) + (stats.reb || 0) + (stats.ast || 0);
    case 'pr':
      return (stats.pts || 0) + (stats.reb || 0);
    case 'pa':
      return (stats.pts || 0) + (stats.ast || 0);
    case 'ra':
      return (stats.reb || 0) + (stats.ast || 0);
    case 'bs':
      return (stats.blk || 0) + (stats.stl || 0);
    default:
      return (stats[statKey as keyof PeriodPlayerStats] as number) ?? 0;
  }
}
